/* UTILS */
import { defineStore } from 'pinia';

import { getCompany } from '@/api/calls/company/company.api';
/* API IMPORTS */
import { getMe } from '@/api/calls/me/me.api';
/* ENUMS */
import { permissionsIdToName } from '@/enums/permissions';
import { getCookie } from '@/helpers/cookies';
import { cloneDeep } from '@/helpers/generic';
/* TYPES */
import type { Auth, AuthHooks, AuthUser, Me, MeManagers } from '@/types/api/me/me';
import type { Company, Licences } from '@/types/company/company.types';

import { useGlobalStore } from './global';

/**
 * This function will map new permissions values into the old and readable one
 */
function getFullPermissions(data: Auth) {
  // replace shortened permission ID to usable name
  const oldPermissions = cloneDeep(data.permissions);

  const newPermissions = oldPermissions.map((p): string => {
    // if not found, will use the value given by the server
    return permissionsIdToName[p] || p;
  });

  /* END OF SOURCING CHECK */

  data.permissions = newPermissions;

  return data;
  // end of shortened permission
}
/**
 * This store contains all data about the current connected user
 */
export const useAuthStore = defineStore('auth', {
  state: (): Me & AuthHooks => {
    return {
      company_uuid: null,
      customer_uuid: '',
      has_manager_accounts: false,
      managers: [] as MeManagers[],
      permissions: [],
      sharepools: [],
      is_2FA_enabled: false,
      is_2FA_required: false,
      is_pole_emploi: false,
      is_recruiter_pe: false,
      user: {} as AuthUser,
      company: {} as Company,
      isLoginDone: false,
      isFirstConnection: false,
      isLoadMeDone: false,
      sourcing: {
        available_credits: 0,
        maximum_credits: 0
      },
      chatbot_ai: {
        current: null,
        next: null,
        total: 0,
        remaining: 0
      }
    };
  },
  getters: {
    /**
     * Get the current locale ID to fetch right translation files
     *
     * @returns {User['locale_id']} The locale ID
     */
    getLocaleId(state) {
      return state.user?.locale_id;
    },
    /**
     * Get the current locale ID to fetch right translation files
     *
     * @returns {User['branding']} Current user branding
     * {
     *      avatar: {String}
     *      cover: {String}
     *      gradients: {Array<String>}
     * }
     */
    getBranding(state) {
      return state.user?.branding;
    },
    /**
     * Get all data about user
     *
     * @returns {User} User's data
     */
    getFullUser(state): AuthUser {
      return state.user;
    },
    /**
     * Get customer UUID
     *
     * @returns {String} customer UUID
     */
    getCustomerUUID(state) {
      return state.customer_uuid;
    },
    /**
     * Is the current connected user a france travail recruiter
     * @returns {boolean} is the current user a france travail recruiter
     */
    getIsFranceTravailRecruiter(state) {
      return state.is_recruiter_pe;
    },
    /**
     * Allows to check if the current user has a given permission
     * @param {String} permission - name of permission to check
     *
     * @returns {Boolean} has the current user the given permission
     */
    hasPermission: state => (permission: string) => {
      return state.permissions?.includes(permission);
    },
    /**
     * Allows to check if the current user has all requested permissions
     * @param {String[]} permissionsList - array of permissions to check
     *
     * @returns {Boolean} true if access granted
     */
    hasPermissionsList: state => (permissionsList: string[]) => {
      // build a new array with granted permissions
      const authorizedPermissions = permissionsList.filter(permission => {
        return state.permissions?.includes(permission) === true;
      });

      // can access if granted permissions are the same than the requested ones
      return authorizedPermissions.length === permissionsList.length;
    },
    /**
     * Get data about user's company
     *
     * @returns {Company} User's data
     */
    getCompany(state) {
      return state.company;
    },
    /**
     * Get company selected
     * @returns string
     */
    getSelectedCompany(state) {
      if (this.isManagerSingleCompany() && state.managers) {
        return state.managers[0]?.company.uuid;
      }
      return getCookie('company_spaces');
    },
    /**
     *
     * @param state
     * @returns
     */
    getMultiCompany: state => {
      return Array.isArray(state.managers) && state.managers.length > 1;
    },
    /**
     *
     * @returns
     */
    isManagerSingleCompany() {
      return !this.getMultiCompany() && !this.hasPermission('company_admin_r') && this.hasPermission('manager_r');
    },
    /**
     * Get data about user's sharepools (only uuids)
     *
     * @returns {String[]} List of user's sharepools
     */
    getSharepools(state) {
      return state.sharepools.map(sharepoolUuid => sharepoolUuid.toLowerCase());
    },
    /**
     * Allows to know if a recruiter is in more than one sharepool
     *
     * @returns {Boolean} true if more than 1
     */
    hasMultipleSharepools(state) {
      return state.sharepools.length > 1;
    },
    /**
     * Get data about recruiter's sharepool, if multiple sharepools, return undefined
     *
     * @returns {String | undefined} Sharepool UUID
     */
    getSharepoolSpace(state) {
      return state.sharepools.length > 1 ? undefined : state.sharepools[0]?.toLowerCase();
    },
    /**
     * Get maximum & available credits for sourcing
     */
    getSourcingCredit(state) {
      return state.sourcing;
    },
    /**
     * Get if a recruiter has reached their contact invitations quota
     */
    hasReachedContactInvitationLimit(state) {
      if (state.company.is_trial && state.company.invitations_quota.remaining <= 0) {
        return true;
      }
      return false;
    },
    /**
     * Get if the company has activated the swipe test
     */
    getIsSwipe(state) {
      return state.company.params.beta?.swipe;
    },
    /**
     * Get chatbot_ai model
     */
    getChatbotAi(state) {
      return state.chatbot_ai;
    },

    /* HOMEMADE HOOKS */
    /**
     * Get if the login is done (used as "hook")
     */
    getIsLoginDone(state) {
      return state.isLoginDone;
    },
    /**
     * Get if the getMe is done (used as "hook")
     */
    getLoadMeDone(state) {
      return state.isLoadMeDone;
    },
    getCompanyUuid(state) {
      return state.company_uuid;
    },
    getPathToSharepoolOrCompany() {
      if (this.getSharepoolSpace) {
        return `sharepool/${this.getSharepoolSpace}`;
      }
      return `company/${this.getCompanyUuid}`;
    },
    getSharepoolOrCompanyParams(): Record<string, string> {
      if (this.getSharepoolSpace) {
        return { sharepool_uuid: this.getSharepoolSpace };
      }
      return { company_uuid: this.getCompanyUuid };
    },
    getIsFirstConnection(state) {
      return state.isFirstConnection;
    }
  },
  actions: {
    /**
     * Set the hook isLoginDone to true
     */
    setLoginDone() {
      this.isLoginDone = true;
    },
    setIsFirstConnection() {
      this.isFirstConnection = true;
    },
    /**
     * Set the hook isGetMeDone to true
     */
    setLoadMeDone() {
      this.isLoadMeDone = true;
    },
    /**
     * Update sourcing credit post invitation
     * @param {Number} creditsLeft - credit left
     */
    setAvailableSourcingCredit(creditsLeft) {
      this.sourcing.available_credits = creditsLeft;
    },
    /**
     * Update the extra data (user_data) inside user instance
     */
    setUserExtraData(newUserData) {
      this.user.user_data = { ...this.user.user_data, ...newUserData };
    },
    setManagerLicences(licences: Licences) {
      this.company.manager_licences = licences;
    },
    setChatbotAiModel(model: string | null) {
      this.chatbot_ai.current = model;
    },
    decrementChatbotAiRemaining() {
      if (this.chatbot_ai.remaining > 0) {
        this.chatbot_ai.remaining -= 1;
      }
    },
    /**
     * Fetch the data about the current connected user
     */
    loadMe() {
      return new Promise<void>((resolve, reject) => {
        getMe()
          .then(async response => {
            this.$patch({
              ...getFullPermissions(response)
            });

            // Redirect to terms of use if needed
            if (this.hasPermission('cst_na')) {
              return window.location.assign(`${useGlobalStore().auth_url}/terms-of-use`);
            }

            // Redirect to MFA if needed
            if (this.hasPermission('mfa_req')) {
              return window.location.assign(`${useGlobalStore().auth_url}/mfa`);
            }
            if (this.$state.is_partner) {
              return window.location.assign(useGlobalStore().auth_url);
            }

            if (this.hasPermission('recruiter_r')) {
              await this.loadCompany();

              resolve();
            } else {
              resolve();
            }
          })
          .catch(error => {
            reject(error);
          });
      });
    },
    /**
     * Refetch the data about the current connected user
     */
    async reloadMe() {
      const response = await getMe();

      this.$patch({
        ...getFullPermissions(response)
      });
    },
    loadCompany() {
      return new Promise<void>(resolve => {
        getCompany(this.company_uuid).then(companyData => {
          this.$patch({
            company: companyData
          });

          resolve();
        });
      });
    },
    /**
     * Allows to fetch user coordinates if not already stored
     * @returns {Promise<{lat: Number, lng: Number}>} user coordinates
     */
    loadUserCurrentLocation(): Promise<{ lat: number; lng: number }> {
      return new Promise(resolve => {
        // If user location is stored, return it

        // Get user coordinates and store them
        navigator.geolocation.getCurrentPosition(
          response => {
            const latLng = { lat: response.coords.latitude, lng: response.coords.longitude };
            this.$patch({ location: latLng });
            resolve(latLng);
          },
          () => {
            resolve(this.getUserLocation);
          }
        );
      });
    }
  }
});
